import { Link, useNavigate } from "react-router-dom";
import { Archive, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  creditsRemaining,
  effectiveCreditsIncluded,
  type AgentStatus,
} from "@/lib/agentsDemoData";
function statusBadge(status: AgentStatus) {
  if (status === "active") return <Badge>Active</Badge>;
  if (status === "paused") return <Badge variant="secondary">Paused</Badge>;
  return <Badge variant="outline">Archived</Badge>;
}

export function AgentsHomePage() {
  const { org, may, viewOnly } = useAgents();
  const navigate = useNavigate();
  const creditsIncluded = effectiveCreditsIncluded(org);
  const remaining = creditsRemaining(org);
  const activeAgents = org.agents.filter((agent) => agent.status !== "archived");
  const archivedAgents = org.agents.filter((agent) => agent.status === "archived");

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Agents</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Hire as many agents as you need. Usage comes from the shared credit pool ({org.planLabel}).
          </p>
        </div>
        <Button
          type="button"
          disabled={!may("create_edit_archive_agents")}
          onClick={() => navigate(agentsHref("/agents/new"))}
          title={
            viewOnly
              ? "Standard role is view only. Ask a Super Admin to add an agent."
              : "Add an agent"
          }
        >
          <Plus className="size-4" aria-hidden />
          Agent
        </Button>
      </div>

      {org.overseer.length > 0 ? (
        <section className="rounded-lg border border-sky-200 bg-sky-50/80 px-4 py-3 dark:border-sky-900/40 dark:bg-sky-950/30">
          <p className="text-sm font-medium text-sky-900 dark:text-sky-100">Overseer highlights</p>
          <ul className="mt-2 space-y-1 text-sm text-sky-800 dark:text-sky-200">
            {org.overseer.slice(0, 3).map((row) => (
              <li key={row.id}>
                <Link to={agentsHref("/overseer")} className="underline underline-offset-2">
                  {row.agentName}
                </Link>
                : {row.summary}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active agents</CardDescription>
            <CardTitle className="text-2xl">{activeAgents.length}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Hire another whenever you need one.
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Credits this month</CardDescription>
            <CardTitle className="text-2xl">
              {org.creditsUsed.toLocaleString()} / {creditsIncluded.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Shared org pool{org.maxAddon ? " · Max (20×) on" : ""}.{" "}
            <Link to={agentsHref("/credits")} className="underline underline-offset-2">
              View credits
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Credits remaining</CardDescription>
            <CardTitle className="text-2xl">{remaining.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            {org.maxAddon ? "Max (20×) is on. " : ""}
            <Link to={agentsHref("/checkout")} className="underline underline-offset-2">
              Open Checkout
            </Link>
          </CardContent>
        </Card>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Active agents
        </h2>
        <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-[#1D232D]">
          {activeAgents.map((agent) => (
            <li key={agent.id}>
              <Link
                to={agentsHref(`/agents/${agent.id}`)}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{agent.name}</p>
                  <p className="text-xs text-gray-500">
                    Owner {agent.humanOwnerName} · {agent.creditsBurnedMonth.toLocaleString()} credits
                    this month
                  </p>
                </div>
                {statusBadge(agent.status)}
              </Link>
            </li>
          ))}
          {activeAgents.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-gray-500">
              No active agents yet. Hire your first one when you are ready.
            </li>
          ) : null}
        </ul>
      </section>

      {archivedAgents.length > 0 ? (
        <section className="space-y-3">
          <h2 className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            <Archive className="size-3.5" aria-hidden />
            Archived
          </h2>
          <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-dashed border-gray-200 bg-white/60 dark:divide-white/10 dark:border-white/10 dark:bg-[#1D232D]/60">
            {archivedAgents.map((agent) => (
              <li key={agent.id}>
                <Link
                  to={agentsHref(`/agents/${agent.id}`)}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  <p className="font-medium text-gray-700 dark:text-gray-300">{agent.name}</p>
                  {statusBadge(agent.status)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
